import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard, GitBranch, Users, FileSearch,
  BookOpen, ClipboardList, AlertCircle, Grid3X3,
  ChevronDown, Shield, Lock,
} from "lucide-react";
import { useStatutory, ROLES } from "./StatutoryContext";
import { SCHEMA_HEAD, IMPL_AUTH_STATUS, MOCK_DISCLAIMER } from "../../mock/statutory/mockData";

const STATUTORY_NAV = [
  { to: "/statutory/overview", label: "Overview", icon: LayoutDashboard, testid: "statutory-nav-overview" },
  { to: "/statutory/regime-assignment", label: "Regime Assignment", icon: GitBranch, testid: "statutory-nav-regime" },
  { to: "/statutory/eligibility", label: "Eligibility", icon: Users, testid: "statutory-nav-eligibility" },
  { to: "/statutory/evidence", label: "Legal Evidence", icon: FileSearch, testid: "statutory-nav-evidence" },
  { to: "/statutory/rule-versions", label: "Rule Versions", icon: BookOpen, testid: "statutory-nav-rules" },
  { to: "/statutory/audit", label: "Audit Log", icon: ClipboardList, testid: "statutory-nav-audit" },
  { to: "/statutory/open-questions", label: "Questions & Blockers", icon: AlertCircle, testid: "statutory-nav-oq" },
  { to: "/statutory/readiness-matrix", label: "Readiness Matrix", icon: Grid3X3, testid: "statutory-nav-matrix" },
];

export default function StatutoryShell({ children }) {
  const { role, setRole, currentRole } = useStatutory();
  const [roleOpen, setRoleOpen] = React.useState(false);
  const location = useLocation();

  return (
    <div className="flex flex-col min-h-full" data-testid="statutory-shell">
      {/* Governance Banner */}
      <div
        className="flex items-center gap-3 px-4 py-2 bg-red-950 border-b border-red-800 text-red-200 text-xs font-mono"
        data-testid="governance-banner"
      >
        <Lock className="h-3 w-3 shrink-0 text-red-400" />
        <span className="font-bold text-red-300">W11-L-IMPL: {IMPL_AUTH_STATUS}</span>
        <span className="text-red-400">·</span>
        <span>Schema: {SCHEMA_HEAD}</span>
        <span className="text-red-400">·</span>
        <span className="text-red-300/70">Frontend Prototype Only — All data is mock/static</span>
      </div>

      {/* Prototype Disclaimer Banner */}
      <div className="flex items-center gap-2 px-4 py-1.5 bg-amber-50 border-b border-amber-200 text-amber-700 text-xs">
        <Shield className="h-3 w-3 shrink-0" />
        <span className="font-medium">PROTOTYPE:</span>
        <span>{MOCK_DISCLAIMER}</span>
      </div>

      {/* Secondary Navigation + Role Switcher */}
      <div className="border-b border-border bg-card/60 sticky top-0 z-10">
        <div className="flex items-center justify-between px-4 md:px-6">
          {/* Tab Navigation */}
          <nav
            className="flex items-center overflow-x-auto scrollbar-hide gap-0"
            data-testid="statutory-subnav"
          >
            {STATUTORY_NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                className={({ isActive }) =>
                  [
                    "flex items-center gap-1.5 px-3 py-3 text-sm whitespace-nowrap border-b-2 transition-colors",
                    isActive
                      ? "border-primary text-primary font-medium"
                      : "border-transparent text-muted-foreground hover:text-foreground hover:border-border",
                  ].join(" ")
                }
              >
                <item.icon className="h-3.5 w-3.5 shrink-0" />
                <span className="hidden md:inline">{item.label}</span>
              </NavLink>
            ))}
          </nav>

          {/* Role Switcher */}
          <div className="relative flex-shrink-0 pl-3" data-testid="role-switcher-container">
            <button
              onClick={() => setRoleOpen(!roleOpen)}
              className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800 hover:bg-amber-100 transition-colors"
              data-testid="role-switcher-button"
            >
              <span className="text-amber-500 font-bold text-[10px] uppercase tracking-wider">Prototype / Mock Role</span>
              <span className="font-semibold">{currentRole.label}</span>
              <ChevronDown className="h-3 w-3" />
            </button>
            {roleOpen && (
              <div className="absolute right-0 top-full mt-1 w-64 rounded-lg border border-border bg-background shadow-lg z-50">
                <div className="p-2 border-b border-border">
                  <p className="text-xs font-bold text-amber-700 uppercase tracking-wider px-2 py-1">
                    Prototype / Mock Role Only
                  </p>
                  <p className="text-xs text-muted-foreground px-2">
                    Does not reflect real authorization. No auth backend.
                  </p>
                </div>
                {Object.values(ROLES).map((r) => (
                  <button
                    key={r.id}
                    onClick={() => { setRole(r.id); setRoleOpen(false); }}
                    className={`w-full text-left px-3 py-2.5 hover:bg-muted transition-colors ${role === r.id ? "bg-primary/5 text-primary" : ""}`}
                    data-testid={`role-option-${r.id}`}
                  >
                    <div className="text-sm font-medium">{r.label}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{r.description}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Page Content */}
      <div className="flex-1 p-4 md:p-6" data-testid="statutory-content">
        {children}
      </div>
    </div>
  );
}
